import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Calendar, Bookmark, ClipboardList, Shield, User, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

const NAVY = "hsl(var(--mc-navy))";

function NavItem({ to, label, icon: Icon, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        [
          "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
          isActive
            ? "bg-white/15 text-white"
            : "text-white/75 hover:text-white hover:bg-white/10",
        ].join(" ")
      }
    >
      <Icon size={16} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isOrganiser = isAdmin || user?.role === "organiser";

  const onLogout = async () => {
    await signOut();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header
        className="w-full text-white"
        style={{ backgroundColor: `hsl(var(--mc-navy))` }}
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6">
          <NavLink to="/events" className="flex items-center gap-3" data-testid="brand-home">
            <img
              src="/mc-crest.png"
              alt="Province of Middlesex crest"
              className="h-9 w-auto"
              data-testid="brand-crest"
            />
            <div className="leading-tight">
              <div className="text-[11px] uppercase tracking-widest text-white/70">Province of Middlesex</div>
              <div className="text-base font-semibold">Middlesex Connect</div>
            </div>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1 ml-6 flex-1">
            <NavItem to="/events" label="Events" icon={Calendar} testId="nav-events" />
            <NavItem to="/bookings" label="My Bookings" icon={Bookmark} testId="nav-bookings" />
            {isOrganiser && <NavItem to="/organise" label="Organise" icon={ClipboardList} testId="nav-organise" />}
            {isAdmin && <NavItem to="/admin" label="Admin" icon={Shield} testId="nav-admin" />}
            <NavItem to="/profile" label="Profile" icon={User} testId="nav-profile" />
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-white/70">Signed in as</div>
              <div className="text-sm font-medium">{user?.name}</div>
            </div>
            <button
              onClick={onLogout}
              data-testid="nav-logout"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 hover:bg-white/10 px-3 py-1.5 text-sm"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden border-t border-white/10">
          <div className="max-w-7xl mx-auto px-3 py-2 flex items-center gap-1 overflow-x-auto">
            <NavItem to="/events" label="Events" icon={Calendar} testId="nav-events-m" />
            <NavItem to="/bookings" label="Bookings" icon={Bookmark} testId="nav-bookings-m" />
            {isOrganiser && <NavItem to="/organise" label="Organise" icon={ClipboardList} testId="nav-organise-m" />}
            {isAdmin && <NavItem to="/admin" label="Admin" icon={Shield} testId="nav-admin-m" />}
            <NavItem to="/profile" label="Profile" icon={User} testId="nav-profile-m" />
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
        <Outlet />
      </main>
      <footer className="max-w-7xl mx-auto w-full px-6 py-4 text-xs text-muted-foreground">
        Middlesex Connect · v2.1.1
      </footer>
      <Toaster />
    </div>
  );
}
